import * as api from "@/services/api";

export const fetchDashboardViews = async ({ commit, state }, { force = false } = {}) => {
  if (state.loaded && !force) return state.views;

  commit("setLoading", true);
  try {
    const response = await api.get("/dashboard/views");
    if (response.data.code == 200) {
      commit("initViews", response.data.data.views);

      // Set first view as active if none selected
      if (!state.activeViewId && response.data.data.views.length > 0) {
        commit("setActiveViewId", response.data.data.views[0].id);
      }
    } else {
      commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Error loading dashboards" }, { root: true });
    }
    return response.data;
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Error loading dashboards" }, { root: true });
    return error;
  } finally {
    commit("setLoading", false);
  }
};

export const createDashboardView = async ({ commit }, payload) => {
  try {
    const response = await api.post("/dashboard/views", payload);
    if (response.data.code == 201) {
      commit("addView", response.data.data.view);
      return response.data.data.view;
    } else {
      commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Error creating dashboard" }, { root: true });
    }
    return response.data;
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Error creating dashboard" }, { root: true });
    return error;
  }
};

export const cloneDashboardView = async ({ commit }, id) => {
  try {
    const response = await api.post(`/dashboard/views/${id}/clone`);
    if (response.data.code == 201) {
      commit("addView", response.data.data.view);
      commit("setActiveViewId", response.data.data.view.id);
      return response.data.data.view;
    } else {
      commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Error cloning dashboard" }, { root: true });
    }
    return response.data;
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Error cloning dashboard" }, { root: true });
    return error;
  }
};

export const updateDashboardView = async ({ commit }, payload) => {
  try {
    const response = await api.patch(`/dashboard/views/${payload.id}`, payload);
    if (response.data.code == 200) {
      commit("updateView", response.data.data.view);
    } else {
      commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Error updating dashboard" }, { root: true });
    }
    return response.data;
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Error updating dashboard" }, { root: true });
    return error;
  }
};

export const saveDashboardLayout = async ({ commit }, { id, layout }) => {
  try {
    const response = await api.patch(`/dashboard/views/${id}/layout`, { layout });
    if (response.data.code == 200) {
      commit("updateViewLayout", { id, layout });
    }
    return response.data;
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Error saving layout" }, { root: true });
    return error;
  }
};

export const updateViewOrder = async ({ commit }, order) => {
  try {
    const response = await api.post("/dashboard/views/order", { order });
    return response.data;
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Error updating order" }, { root: true });
    return error;
  }
};

export const adoptDashboardTemplate = async ({ commit }, { templateId, assignType = "private", departmentIds = [] }) => {
  try {
    const payload = { assign_type: assignType };
    if (assignType === "department" && departmentIds.length) payload.department_ids = departmentIds;
    const response = await api.post(`/dashboard/views/templates/${templateId}/adopt`, payload);
    if (response.data.code === 201 || response.data.code === 200) {
      commit("addView", response.data.data.view);
      return response.data.data.view;
    }
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Error adopting template" }, { root: true });
    return null;
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Error adopting template" }, { root: true });
    return null;
  }
};

export const syncViewTemplate = async ({ commit }, viewId) => {
  try {
    const response = await api.post(`/dashboard/views/${viewId}/sync-template`);
    if (response.data.code === 200) {
      commit("updateView", response.data.data.view);
      return response.data.data.view;
    }
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Could not apply template update." }, { root: true });
    return null;
  } catch {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Could not apply template update." }, { root: true });
    return null;
  }
};

export const deleteDashboardView = async ({ commit, state }, id) => {
  try {
    const response = await api.destroy(`/dashboard/views/${id}`);
    if (response.data.code == 200) {
      commit("deleteView", id);

      // If deleted view was active, switch to first available
      if (state.activeViewId === id) {
        const remaining = Object.values(state.views);
        if (remaining.length > 0) {
          commit("setActiveViewId", remaining[0].id);
        }
      }
    } else {
      commit("ui/setSnackbar", { variant: "danger", title: "Error", message: response.data.message || "Error deleting dashboard" }, { root: true });
    }
    return response.data;
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Error deleting dashboard" }, { root: true });
    return error;
  }
};
